import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useEnhancedBrandStore } from '../../../stores/enhancedBrandStore'
import { XMarkIcon } from '@heroicons/react/24/outline'
import echarts from '../../../lib/echarts'

const CATEGORY_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
  '#14b8a6', '#eab308', '#dc2626', '#7c3aed', '#0891b2',
  '#65a30d', '#ea580c', '#db2777', '#4f46e5', '#0d9488'
]

export function InteractiveCategoriesChart() {
  const { filteredBrands, allBrands, filters, setProductCategoriesFilter } = useEnhancedBrandStore()

  const chartData = useMemo(() => {
    // Use allBrands for stable treemap layout, but show filtered counts in tooltip
    const allCategoryCounts = allBrands.reduce((acc, brand) => {
      brand.clean_categories.forEach(category => {
        acc[category] = (acc[category] || 0) + 1
      })
      return acc
    }, {} as Record<string, number>)

    const filteredCategoryCounts = filteredBrands.reduce((acc, brand) => {
      brand.clean_categories.forEach(category => {
        acc[category] = (acc[category] || 0) + 1
      })
      return acc
    }, {} as Record<string, number>)

    // Convert to array and sort by total count for stable layout
    const sortedCategories = Object.entries(allCategoryCounts)
      .sort(([,a], [,b]) => b - a)
      .filter(([,count]) => count > 0)

    return sortedCategories.map(([name, value], index) => ({
      name,
      value, // Total count for sizing
      filteredValue: filteredCategoryCounts[name] || 0, // Filtered count for display
      itemStyle: {
        color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
        shadowBlur: filters.productCategories.length > 0 && filters.productCategories.includes(name) ? 8 : 0,
        shadowColor: filters.productCategories.length > 0 && filters.productCategories.includes(name) ? 'rgba(0, 0, 0, 0.4)' : 'transparent',
        opacity: filters.productCategories.length === 0 || filters.productCategories.includes(name) ? 1 : 0.6
      }
    }))
  }, [allBrands, filteredBrands, filters.productCategories])

  const option = {
    animation: true,
    animationDuration: 300,
    animationEasing: 'cubicOut',
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const dataItem = chartData.find(item => item.name === params.name)
        if (!dataItem) return params.name
        
        const totalPercentage = ((params.value / allBrands.length) * 100).toFixed(1)
        const filteredPercentage = filteredBrands.length > 0 ? ((dataItem.filteredValue / filteredBrands.length) * 100).toFixed(1) : '0'
        
        const hasFilters = filters.productCategories.length > 0 || 
                          filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.states.length > 0
        
        if (hasFilters && dataItem.filteredValue !== params.value) {
          return `<strong>${params.name}</strong><br/>` +
                 `${dataItem.filteredValue} of ${params.value} brands<br/>` +
                 `<span style="color: #059669;">${totalPercentage}% of all brands</span><br/>` +
                 `<span style="color: #6b7280;">${filteredPercentage}% of filtered view</span>`
        } else {
          return `<strong>${params.name}</strong><br/>${params.value} brands (${totalPercentage}%)`
        }
      }
    },
    series: [
      {
        type: 'treemap',
        top: '5%',
        left: '5%',
        right: '5%',
        bottom: '5%',
        width: '90%',
        height: '90%',
        roam: false,
        nodeClick: true,
        breadcrumb: {
          show: false
        },
        label: {
          show: true,
          formatter: (params: any) => {
            // Only show labels for reasonably sized areas
            const minAreaForText = allBrands.length * 0.015
            if (params.value < minAreaForText) {
              return ''
            }
            
            const percentage = ((params.value / allBrands.length) * 100).toFixed(1)
            
            // Smart text sizing based on area
            let name = params.name
            
            if (params.value > allBrands.length * 0.08) {
              // Large areas: full name
              if (name.length > 20) name = name.substring(0, 17) + '...'
            } else if (params.value > allBrands.length * 0.04) {
              // Medium areas: abbreviated name
              if (name.length > 15) name = name.substring(0, 12) + '...'
            } else {
              // Small areas: very short name
              if (name.length > 10) name = name.substring(0, 8) + '...'
            }
            
            return `${name}\n${params.value} (${percentage}%)`
          },
          fontSize: 10,
          color: '#fff',
          fontWeight: 'bold',
          textShadowColor: 'rgba(0, 0, 0, 0.8)',
          textShadowBlur: 2,
          textShadowOffsetX: 1,
          textShadowOffsetY: 1,
          lineHeight: 14,
          rich: {
            name: {
              fontSize: 11,
              fontWeight: 'bold'
            },
            count: {
              fontSize: 9,
              fontWeight: 'normal'
            }
          }
        },
        upperLabel: {
          show: false
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
          gapWidth: 1
        },
        animationDuration: 300,
        animationEasing: 'cubicOut',
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          },
          label: {
            fontSize: 11,
            fontWeight: 'bold',
            formatter: (params: any) => {
              const percentage = ((params.value / filteredBrands.length) * 100).toFixed(1)
              return `${params.name}\n${params.value} (${percentage}%)`
            }
          }
        },
        data: chartData
      }
    ]
  }

  const handleChartClick = (params: any) => {
    const clickedCategory = params.name
    if (!clickedCategory) return

    const currentFilters = filters.productCategories
    const isSelected = currentFilters.includes(clickedCategory)

    if (isSelected) {
      setProductCategoriesFilter(currentFilters.filter(f => f !== clickedCategory))
    } else {
      setProductCategoriesFilter([...currentFilters, clickedCategory])
    }
  }

  const clearFilter = (filterValue: string) => {
    setProductCategoriesFilter(filters.productCategories.filter(f => f !== filterValue))
  }

  const onEvents = {
    click: handleChartClick
  }

  const hasActiveFilters = filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.productCategories.length > 0 || 
                          filters.states.length > 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Chart */}
      <div className="space-y-3">
        {hasActiveFilters && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded text-xs font-medium">
              FILTERED VIEW
            </div>
            <span className="text-gray-600 dark:text-gray-400">
              Showing {filteredBrands.length} of {allBrands.length} brands
            </span>
          </div>
        )}
        <div className="h-80">
          <ReactECharts
            option={option}
            style={{ height: '100%', width: '100%' }}
            onEvents={onEvents}
            echarts={echarts}
          />
        </div>
      </div>

      {/* Active Filters */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Active Category Filters
          </h4>
          {filters.productCategories.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No filters applied. Click treemap areas to filter.
            </p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {filters.productCategories.map((filter, index) => (
                <div
                  key={filter}
                  className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/30 px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length] }}
                    />
                    <span className="text-sm text-purple-700 dark:text-purple-300">
                      {filter}
                    </span>
                  </div>
                  <button
                    onClick={() => clearFilter(filter)}
                    className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setProductCategoriesFilter([])}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Clear all category filters
              </button>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            💡 <strong>Tip:</strong> Click any area in the treemap to filter brands by that product category.
          </p>
        </div>
      </div>
    </div>
  )
}